import React from 'react';
import IconSystem from 'icon-collection/icon-systems';
import PropertyFilterField from './PropertyFilterField';
import DateRangePresetSelector from './DateRangePresetSelector';
import type { DateRangePreset } from 'hooks/useDateRangePreset';

interface Props {
  /** Selected property GUID. Empty string = none selected. */
  propertyId: string;
  onPropertyChange: (id: string) => void;

  preset: DateRangePreset;
  from: string;
  to: string;
  onPresetChange: (p: DateRangePreset) => void;
  onFromChange: (date: string) => void;
  onToChange: (date: string) => void;

  /** Apply / Refresh button click. The bar has no internal "apply pending"
   *  state — consumers are expected to refetch on change OR on this click. */
  onRefresh?: () => void;

  /** Slot for additional report-specific fields (rate plan, source, etc.). */
  children?: React.ReactNode;
}

/**
 * Standard top-of-screen filter bar shared by every dashboard + report.
 * Property + date range are the two mandatory dimensions; per-report extras
 * slot in via `children`.
 */
const ReportFilterBar: React.FC<Props> = ({
  propertyId, onPropertyChange,
  preset, from, to, onPresetChange, onFromChange, onToChange,
  onRefresh, children,
}) => (
  <div className="report-filter-bar">
    <PropertyFilterField value={propertyId} onChange={onPropertyChange} />

    <DateRangePresetSelector
      preset={preset}
      from={from}
      to={to}
      onPresetChange={onPresetChange}
      onFromChange={onFromChange}
      onToChange={onToChange}
    />

    {children}

    {onRefresh && (
      <div className="filter-field--actions">
        <button
          type="button"
          className="btn btn-primary"
          onClick={onRefresh}
          disabled={!propertyId}
          title={propertyId ? 'Refresh data' : 'Pick a property to enable'}
        >
          <IconSystem.refresh size={14} /> &nbsp;Refresh
        </button>
      </div>
    )}
  </div>
);

export default ReportFilterBar;
